import React, { useState, useEffect, useRef } from 'react';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Modal from '@salesforce/design-system-react/components/modal';
import Button from '@salesforce/design-system-react/components/button';
import Combobox from '@salesforce/design-system-react/components/combobox';
import ColorPicker from '@salesforce/design-system-react/components/color-picker';
import Textarea from '@salesforce/design-system-react/components/textarea';
import Input from '@salesforce/design-system-react/components/input';
import { Formik } from 'formik';
import { addNewContact, contactSelector } from '../../store/contact/slice';
import { useDispatch, useSelector } from 'react-redux';
import { contactValidation } from './contactValidation';
import toast, { Toaster } from 'react-hot-toast';
import { resolveLocalVariables } from '../../init';

const walletType = [
  { key: 1, id: '1', label: 'Defi Wallet', value: 'A0' },
  { key: 2, id: '2', label: 'Exchange Wallet', value: 'B0' },
];

const category = [
  { id: '1', label: 'Employee', value: 'A0' },
  { id: '2', label: 'Supplier', value: 'B0' },
  { id: '3', label: 'OTC', value: 'C0' },
];

const acceptedAssets = [
  {
    id: '1',
    label: 'USDC',
    value: 'A0',
  },
  {
    id: '2',
    label: 'USDT',
    value: 'B0',
  },
  {
    id: '3',
    label: 'L3USD',
    value: 'C0',
  },
  {
    id: '4',
    label: 'DAI',
    value: 'D0',
  },
  {
    id: '5',
    label: 'MIM',
    value: 'E0',
  },
  {
    id: '6',
    label: 'LIF3',
    value: 'F0',
  },
  {
    id: '7',
    label: 'LSHARE',
    value: 'G0',
  },
  {
    id: '8',
    label: 'TOMB',
    value: 'H0',
  },
  {
    id: '9',
    label: 'TBOND',
    value: 'I0',
  },
  {
    id: '10',
    label: 'FTM',
    value: 'J0',
  },
  {
    id: '11',
    label: 'ETH',
    value: 'K0',
  },
  {
    id: '12',
    label: 'BTC',
    value: 'L0',
  },
  {
    id: '13',
    label: 'TSHARE',
    value: 'M0',
  },
  {
    id: '14',
    label: 'TSHARE',
    value: 'N0',
  },
  {
    id: '15',
    label: 'AVAX',
    value: 'O0',
  },
  {
    id: '16',
    label: 'BNB',
    value: 'P0',
  },
  {
    id: '17',
    label: 'LINK',
    value: 'Q0',
  },
  {
    id: '18',
    label: 'ZOO',
    value: 'R0',
  },
];

const acceptedChain = [
  {
    id: '1',
    label: 'Ethereum',
    logo_url:
      'https://static.debank.com/image/chain/logo_url/eth/42ba589cd077e7bdd97db6480b0ff61d.png',
    value: 'A0',
  },
  {
    id: '2',
    label: 'BSC',
    logo_url:
      'https://static.debank.com/image/chain/logo_url/bsc/7c87af7b52853145f6aa790d893763f1.png',
    value: 'B0',
  },
  {
    id: '3',
    label: 'Gnosis',
    logo_url:
      'https://static.debank.com/image/chain/logo_url/xdai/8b5320523b30bd57a388d1bcc775acd5.png',
    value: 'C0',
  },
  {
    id: '4',
    label: 'Polygon',
    logo_url:
      'https://static.debank.com/image/chain/logo_url/matic/d3d807aff1a13e9ba51a14ff153d6807.png',
    value: 'D0',
  },
  {
    id: '5',
    label: 'Fantom',
    value: 'E0',
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const AddContact = (props) => {
  const { openModal, closeModal } = props;
  const [categorySelection, setCategorySelection] = useState([]);
  const [walletTypeSelection, setWalletTypeSelection] = useState([]);
  const [aceptedAssetsSelection, setAcceptedAssetsSelection] = useState([]);
  const [chainSelection, setChainSelection] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [formData, setFormData] = useState({});
  const dispatch = useDispatch();
  const { loading, success, message } = useSelector(contactSelector);
  const formikRef = useRef();

  useEffect(() => {
    setIsOpen(openModal);
  }, [openModal]);

  useEffect(() => {
    success && !loading && showToast &&
      toast.success(message, {
        id: 'add-contact',
      });
    setShowToast(false);
  }, [success, showToast, message, loading]);

  const toggleOpen = () => {
    resetFormValues();
    closeModal();
  };

  const resetFormValues = () => {
    formikRef?.current?.resetForm();
    setCategorySelection([]);
    setWalletTypeSelection([]);
    setAcceptedAssetsSelection([]);
    setChainSelection([])
  }

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={{
        name: '',
        address: '',
        walletType: '',
        contactCategory: '',
        acceptedAssets: '',
        chain: '',
        description: '',
        color: '',
      }}
      validationSchema={contactValidation({
        type: 'Add'
      })}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        const data = {
          name: values.name,
          address: values.address,
          wallet_type: values.walletType.length ? values.walletType[0]?.label : '',
          contact_category: values.contactCategory.length ? values.contactCategory[0]?.label : '',
          description: values.description,
          color: values.color,
          chain: values.chain.length ? values.chain[0]?.label : '',
          accepted_assets: values.acceptedAssets.length ? values.acceptedAssets[0]?.label : '',
        };

        /**
         * check if key is empty, and remove empty key.
         */
        Object.keys(data).forEach((key) => {
          if (!data[key]) delete data[key];
        });

        dispatch(addNewContact(data)).then((res) => {
          if (res?.payload?.data) {
            setShowToast(true);
            resetForm();
            resetFormValues()
            closeModal();
            resolveLocalVariables();
          }
        })
      }}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        } = props;
        return (
          <div>
            <Modal
              ariaHideApp={false}
              isOpen={isOpen}
              footer={[
                <Button className='modal-cancel-button' key="cancel" label="Cancel" onClick={toggleOpen} />,
                <IconSettings iconPath="/assets/icons">
                  <Button
                    className='modal-save-button'
                    key="save"
                    label={loading ? <span>Loading</span> : <span>Save</span>}
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    iconCategory='utility'
                    iconName='save'
                    iconPosition='left'
                  />
                </IconSettings>,
              ]}
              onRequestClose={toggleOpen}
              heading="Add New Contact"
            >
              <section className="slds-p-around_large">
                <form
                  onSubmit={handleSubmit}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                >
                  <Toaster position="top-right" reverseOrder={false} />
                  <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="name"
                        name="name"
                        label="Contact Name"
                        placeholder="Enter Contact Name"
                        required
                        value={values.name || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(errors.name && touched.name && 'slds-has-error')}
                      />
                      {errors.name && touched.name && (
                        <div className="slds-form-element__help">{errors.name}</div>
                      )}
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <Input
                        id="address"
                        name="address"
                        label="Address"
                        required
                        placeholder="Enter Address"
                        value={values.address || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={classNames(
                          errors.address && touched.address && 'slds-has-error'
                        )}
                      />
                      {errors.address && touched.address && (
                        <div className="slds-form-element__help">{errors.address}</div>
                      )}
                    </div>
                  </div>

                  <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <IconSettings iconPath="/assets/icons">
                        <Combobox
                          id="walletType"
                          name="walletType"
                          required
                          events={{
                            onSelect: (event, data) => {
                              const selection =
                                data.selection.length === 0 ? walletTypeSelection : data.selection;
                              setFieldValue('walletType', selection);
                              setWalletTypeSelection(selection);
                            },
                          }}
                          labels={{
                            label: 'Wallet Type',
                            placeholder: 'Select Wallet Type',
                          }}
                          options={walletType}
                          selection={walletTypeSelection}
                          variant="readonly"
                          className={classNames(
                            errors.walletType && touched.walletType && 'slds-has-error'
                          )}
                        />
                      </IconSettings>
                      {errors.walletType && touched.walletType && (
                        <div className="slds-form-element__help">{errors.walletType}</div>
                      )}
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <IconSettings iconPath="/assets/icons">
                        <Combobox
                          id="contactCategory"
                          name="contactCategory"
                          required
                          events={{
                            onSelect: (event, data) => {
                              const selection =
                                data.selection.length === 0 ? categorySelection : data.selection;
                              setCategorySelection(selection);
                              setFieldValue('contactCategory', selection);
                            },
                          }}
                          labels={{
                            label: 'Contact Category',
                            placeholder: 'Select Contact Category',
                          }}
                          options={category}
                          selection={categorySelection}
                          variant="readonly"
                          className={classNames(
                            errors.contactCategory && touched.contactCategory && 'slds-has-error'
                          )}
                        />
                      </IconSettings>
                      {errors.contactCategory && touched.contactCategory && (
                        <div className="slds-form-element__help">{errors.contactCategory}</div>
                      )}
                    </div>
                  </div>

                  <div className="two-grid-layout slds-grid_pull-padded slds-grid_vertical-align-center">
                    <div className="slds-col_padded slds-m-bottom_large">
                      <IconSettings iconPath="/assets/icons">
                        <Combobox
                          id="acceptedAssets"
                          name="acceptedAssets"
                          required
                          events={{
                            onSelect: (event, data) => {
                              const selection =
                                data.selection.length === 0
                                  ? aceptedAssetsSelection
                                  : data.selection;
                              setFieldValue('acceptedAssets', selection);
                              setAcceptedAssetsSelection(selection);
                            },
                          }}
                          labels={{
                            label: 'Accepted Assets',
                            placeholder: 'Select Accepted Assets',
                          }}
                          options={acceptedAssets}
                          selection={aceptedAssetsSelection}
                          variant="readonly"
                          className={classNames(
                            errors.acceptedAssets && touched.acceptedAssets && 'slds-has-error'
                          )}
                        />
                      </IconSettings>
                      {errors.acceptedAssets && touched.acceptedAssets && (
                        <div className="slds-form-element__help">{errors.acceptedAssets}</div>
                      )}
                    </div>
                    <div className="slds-col_padded slds-m-bottom_large">
                      <IconSettings iconPath="/assets/icons">
                        <Combobox
                          id="chain"
                          name="chain"
                          required
                          events={{
                            onSelect: (event, data) => {
                              const selection =
                                data.selection.length === 0 ? chainSelection : data.selection;
                              setChainSelection(selection);
                              setFieldValue('chain', selection);
                            },
                          }}
                          labels={{
                            label: 'Chain',
                            placeholder: 'Selected Chain',
                          }}
                          options={acceptedChain}
                          selection={chainSelection}
                          variant="readonly"
                          className={classNames(errors.chain && touched.chain && 'slds-has-error')}
                        />
                      </IconSettings>
                      {errors.chain && touched.chain && (
                        <div className="slds-form-element__help">{errors.chain}</div>
                      )}
                    </div>
                  </div>

                  <div className="two-grid-layout slds-grid_pull-padded">
                    <div className="slds-col_padded" style={{ alignSelf: 'normal' }}>
                      <div className="slds-form-element">
                        <div className="slds-form-element__control">
                          <Textarea
                            id="description"
                            name="description"
                            label="Description"
                            placeholder="Enter description"
                            value={values.description || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="slds-col_padded">
                      <div className="slds-m-bottom_large">
                        <div className="">
                          <IconSettings iconPath="/assets/icons">
                            <ColorPicker
                              labels={{ label: 'Color' }}
                              id="color"
                              name="color"
                              className={'slds-form-element__label'}
                              menuPosition="overflowBoundaryElement"
                              value=""
                              events={{
                                onChange: (event, { color }) => {
                                  setFieldValue('color', color);
                                  setFormData({
                                    ...formData,
                                    color,
                                  });
                                },
                              }}
                            />
                          </IconSettings>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </section>
            </Modal>
          </div>
        );
      }}
    </Formik>
  );
};

export default AddContact;
